import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { Popup } from "@progress/kendo-react-popup";
import styles from "pages/CaseFile/Casefile.module.scss";
import { MenuList } from "components/CaseFiles/CaseFilesHeader";
import { BsThreeDots } from "react-icons/bs";
import { LuFileSignature } from "react-icons/lu";
import { MdOutlinePreview } from "react-icons/md";
import { LiaDownloadSolid, LiaEditSolid, LiaReceiptSolid } from "react-icons/lia";
import { IoDuplicateOutline } from "react-icons/io5";
import { HiOutlineDocumentText } from "react-icons/hi2";
import { VscFilePdf } from "react-icons/vsc";
import { RiShareForward2Fill } from "react-icons/ri";
import { RiDeleteBin6Line, RiFileTransferLine } from "react-icons/ri";
import { TbPinned, TbPinnedOff } from "react-icons/tb";
import { getSelectedActivities } from "./activityUtils";

const MenuCell = ({
  setViewActivity,
  setView,
  showDocumentFile,
  handleDownload,
  getDocumentEditorData,
  handleDuplicate,
  handleMoveCopyActivity,
  setShowShareDocument,
  setCurrentDoc,
  onDeleteClick,
  setESigning,
  setAddActivity,
  loaded,
  setLoaded,
  handleActivityRowClick,
  selectedState,
  setCurrentMatterForShare,
  activities,
  onAttachToChit,
  onTogglePin,
  noTd = false,
  ...props
}) => {
  const { activityId, documentId, documentName, matterId, matterRef } =
    props.dataItem;
  const popupVisible = props.openMenuActivityId === activityId;
  const setPopupVisible = (next) => {
    const value = typeof next === "function" ? next(popupVisible) : next;
    if (value) {
      props.setOpenMenuActivityId?.(activityId);
    } else {
      props.setOpenMenuActivityId?.((current) =>
        current === activityId ? null : current,
      );
    }
  };
  const [anchorEl, setAnchorEl] = useState(null);
  const anchor = useRef();
  const setAnchorRef = (node) => {
    anchor.current = node;
    setAnchorEl(node);
  };
  const closeTimerRef = useRef(null);
  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setPopupVisible(false);
      closeTimerRef.current = null;
    }, 200);
  };
  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const selectedActivitiesWithDocs = React.useMemo(() => {
    const ids = Object.keys(selectedState || {}).filter((k) => selectedState[k]);
    return ids
      .map((id) => activities?.find((a) => a.activityId == id))
      .filter((a) => a && a.documentId && a.documentName);
  }, [selectedState, activities]);
  const canAttachToChit = selectedActivitiesWithDocs.length > 0;
  const isMultipleSelected =
    Object.keys(selectedState || {}).filter((k) => selectedState[k]).length > 1;

  const pinTargets = React.useMemo(
    () => getSelectedActivities(props.dataItem, selectedState || {}, activities),
    [props.dataItem, selectedState, activities],
  );
  const allPinned = pinTargets.length > 0 && pinTargets.every((a) => a.pinned);
  const pinLabel = `${allPinned ? "Unpin" : "Pin"} ${
    pinTargets.length > 1 ? "Activities" : "Activity"
  }`;

  const handleMenuClick = (e) => {
    e.stopPropagation();
    if (popupVisible) {
      setPopupVisible(false);
      return;
    }
    const isSelected = selectedState[activityId];
    if (!isSelected) {
      props.setSelectedState({ [activityId]: true });
      props.setMultiSel(false);
      if (props.isDocViewerOpen) {
        props.showPaneFile(documentId);
      }
    }
    setPopupVisible(true);
  };

  useEffect(() => {
    if (!popupVisible) return;

    const closePopup = (e) => {
      if (anchor.current && anchor.current.contains(e.target)) return;
      setPopupVisible(false);
    };

    document.addEventListener("click", closePopup);
    return () => {
      document.removeEventListener("click", closePopup);
    };
  }, [popupVisible]);

  const Wrapper = noTd ? "div" : "td";
  return (
    <Wrapper
      className={noTd ? undefined : styles.menuCell}
      onMouseLeave={() => {
        loaded && setLoaded(false);
      }}>
      {popupVisible && (
        <Popup
          show={true}
          anchor={anchorEl}
          appendTo={typeof document !== "undefined" ? document.body : undefined}
          popupClass={`${styles["popup-content"]} ${styles["menu-popup"]}`}
          anchorAlign={{ horizontal: "right", vertical: "center" }}
          popupAlign={{ horizontal: "left", vertical: "center" }}
          animate={false}
          style={{ zIndex: 10003 }}>
          <div
            className={`mb-3 ${styles["menu-popup-body"]}`}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            onClick={(e) => { e.stopPropagation(); setPopupVisible(false); }}>
            <div className={styles["border-bottom"]}></div>
            <div className="flex flex-column mt-4 justify-start w-100">
              {!isMultipleSelected && (
                <MenuList
                  border={true}
                  icon={<MdOutlinePreview role="button" size={22} color="black" />}
                  name="View Activity"
                  noMargins
                  onClick={() => {
                    if (handleActivityRowClick) {
                      handleActivityRowClick(props.dataItem);
                    } else {
                      setAddActivity(true);
                      setViewActivity(props.dataItem);
                      setView(true);
                    }
                  }}
                />
              )}
              {onTogglePin && (
                <MenuList
                  border={true}
                  noMargins
                  icon={
                    allPinned ? (
                      <TbPinnedOff role="button" size={22} strokeWidth={1.5} color="black" />
                    ) : (
                      <TbPinned role="button" size={22} strokeWidth={1.5} color="black" />
                    )
                  }
                  name={pinLabel}
                  onClick={() => onTogglePin(pinTargets, !allPinned)}
                />
              )}
              {documentId && documentName && (
                <>
                  {!isMultipleSelected && (
                    <MenuList
                      border={true}
                      noMargins
                      icon={
                        <HiOutlineDocumentText role="button" size={22} color="black" />
                      }
                      name="Preview Document"
                      onClick={() => showDocumentFile(documentId)}
                    />
                  )}
                  <MenuList
                    noMargins
                    border={true}
                    name="Download"
                    icon={<LiaDownloadSolid role="button" size={22} />}
                    onClick={() => handleDownload(documentId)}
                  />
                  {canAttachToChit && (
                    <MenuList
                      noMargins
                      border={true}
                      name="Attach to Chit"
                      icon={<LiaReceiptSolid role="button" size={22} color="black" />}
                      onClick={() => {
                        const docs = selectedActivitiesWithDocs.map((a) => ({
                          documentId: a.documentId,
                          documentName: a.documentName,
                        }));
                        setPopupVisible(false);
                        onAttachToChit?.(docs);
                      }}
                    />
                  )}
                  {(documentName.includes(".docx") ||
                    documentName.includes(".doc")) && (
                    <MenuList
                      noMargins
                      border={true}
                      name="Edit"
                      icon={<LiaEditSolid role="button" size={22} />}
                      onClick={() => {
                        if (setCurrentMatterForShare && matterId) {
                          setCurrentMatterForShare({
                            matterId: matterId,
                            matterRef: matterRef,
                          });
                        }
                        getDocumentEditorData(documentId);
                      }}
                    />
                  )}
                  {!isMultipleSelected && (
                    <MenuList
                      noMargins
                      border={true}
                      name="Duplicate"
                      icon={<IoDuplicateOutline role="button" size={22} />}
                      onClick={() =>
                        handleDuplicate(
                          props?.matterId ?? props.dataItem?.matterId,
                          props.dataItem,
                        )
                      }
                    />
                  )}
                  {!documentName?.includes(".pdf") && (
                    <MenuList
                      noMargins
                      border={true}
                      name="Download as PDF"
                      icon={<VscFilePdf role="button" size={22} />}
                      onClick={() => handleDownload(documentId, true)}
                    />
                  )}

                  <MenuList
                    noMargins
                    border={true}
                    icon={<RiShareForward2Fill role="button" size={20} />}
                    name="Share"
                    onClick={() => {
                      setShowShareDocument(true);
                      setCurrentDoc({ id: documentId, name: documentName });
                      if (setCurrentMatterForShare && matterId) {
                        setCurrentMatterForShare({
                          matterId: matterId,
                          matterRef: matterRef,
                        });
                      }
                    }}
                  />
                </>
              )}

              {documentId && documentName && (
                <MenuList
                  border={true}
                  noMargins
                  icon={<LuFileSignature role="button" size={22} color="black" />}
                  name="E Signing"
                  onClick={() => {
                    setESigning(activityId);
                    if (setCurrentMatterForShare && matterId) {
                      setCurrentMatterForShare({
                        matterId: matterId,
                        matterRef: matterRef,
                      });
                    }
                  }}
                />
              )}
              {!isMultipleSelected && handleMoveCopyActivity && (
                <MenuList
                  noMargins
                  border={true}
                  name="Move Activity"
                  icon={<RiFileTransferLine role="button" size={20} />}
                  onClick={() => handleMoveCopyActivity(props.dataItem)}
                />
              )}
              <MenuList
                noMargins
                border={false}
                name="Delete"
                icon={<RiDeleteBin6Line role="button" size={18} />}
                onClick={() => onDeleteClick?.(props.dataItem)}
              />
            </div>
          </div>
        </Popup>
      )}
      <div
        ref={setAnchorRef}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={handleMenuClick}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        role="button"
        aria-label="Activity actions"
        className={`${styles.menuTrigger} ${
          selectedState[activityId] || popupVisible ? "" : styles.menuTriggerHidden
        }`}>
        <BsThreeDots color="#323130" size={20} aria-hidden="true" />
      </div>
    </Wrapper>
  );
};

export default MenuCell;
